import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './PACSModalSideBar.module.scss';
import { PACS_CONTENTS_NAMES } from 'config/PACSConfig';
import Button from '@components/Button';
import Input from '@components/Input';
import { DatePicker, DateRangePicker } from '@components/DatePicker/DatePicker';
import {
  Option,
} from '@components/MultiSelectDropdown/MultiSelectDropdown';
import { DicomNodeBase, PacsParams } from '@models/pacs';
import { useModal } from '@components/Modal/ModalProvider';
import SettingModal from '@features/analysis/analysisModal/SettingsModal/SettingsModal';
import Dropdown from '@components/Dropdown';
interface PACSModalSideBarProps {
  onSubmitQuery: (query_info: PacsParams) => void;
  DicomNodes: DicomNodeBase[]
  setDicomNode: React.Dispatch<React.SetStateAction<string >>;
  onClearAll: () => void;
}

interface OptionD {
  value: string;
  label: string;
}

const PACSModalSIdeBar = ({
  onSubmitQuery,
  DicomNodes,
  setDicomNode,
  onClearAll
}: PACSModalSideBarProps) => {
  const [selectedOption, setSelectedOption] = useState<OptionD[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [patientName, setPatientName] = useState<string>('');
  const [patientId, setPatientId] = useState<string>('');
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [accessionNumber, setAccessionNumber] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [referringPhysician, setReferringPhysician] = useState<string>('');
  const [studyId, setStudyId] = useState<string>('');
  const [studyDateRange, setStudyDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });
  const { openModal, closeModal } = useModal();
  const settingsModalIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [Modalities, setModalities] = useState<Option[]>([
    { value: '1', label: 'CR', isSelected: false },
    { value: '2', label: 'PT', isSelected: false },
    { value: '3', label: 'XC', isSelected: false },
    { value: '4', label: 'CT', isSelected: false },
    { value: '5', label: 'SR', isSelected: false },
    { value: '6', label: 'VL', isSelected: false },
    { value: '7', label: 'MG', isSelected: false },
    { value: '8', label: 'SC', isSelected: false },
    { value: '9', label: 'US', isSelected: false },
    { value: '10', label: 'XA', isSelected: false },
    { value: '11', label: 'MP', isSelected: false },
    { value: '12', label: 'PX', isSelected: false },
    { value: '13', label: 'RF', isSelected: false },
    { value: '14', label: 'AU', isSelected: false },
    { value: '15', label: 'NM', isSelected: false },
    { value: '16', label: 'OT', isSelected: false },
    { value: '17', label: 'DX', isSelected: false },
    { value: '18', label: 'RG', isSelected: false },
    { value: '19', label: 'ES', isSelected: false },
    { value: '20', label: 'DR', isSelected: false },
  ]);

  useEffect(() => {
    if (DicomNodes && DicomNodes.length > 0) {
      const options = DicomNodes.map((node) => ({
        value: String(node.id),
        label: String(node.description ?? ''),
      }));
      setSelectedOption(options);
      setSelectedValue(options[0]?.value ?? '');
    }
  }, [DicomNodes]);

  const handleOpenSettingsModal = useCallback(
    (initialTabId?: string) => {
      const id = openModal(
        <SettingModal
          {...(initialTabId ? { initialTabId } : {})}
          onClose={() => closeModal(id)}
        />,
      );
      settingsModalIdRef.current = id;
    },
    [openModal, closeModal],
  );

  const canSubmitQuery =
    selectedValue !== '' &&
    (
      patientName.trim() !== '' ||
      patientId.trim() !== '' ||
      !!birthDate ||
      studyId.trim() !== '' ||
      !!(studyDateRange.startDate || studyDateRange.endDate)
    );

  const handleQueryClick = useCallback(() => {
    if (!canSubmitQuery) return;

    const cleanStartDate = studyDateRange.startDate?.replace(/-/g, '') ?? '';
    const cleanEndDate = studyDateRange.endDate?.replace(/-/g, '') ?? '';
    const cleanBirthDate = birthDate?.replace(/-/g, '') ?? '';

    const newFormData: PacsParams = {
      SelectServer: selectedValue ?? '',
      PatientName: patientName,
      PatientID: patientId,
      BirthDate: cleanBirthDate ?? '',
      AccessionNumber: accessionNumber,
      Description: description,
      ReferringPhysician: referringPhysician,
      StudyID: studyId,
      StudyUID: '',
      SeriesUID: '',
      StudyDate: cleanStartDate && cleanEndDate ? `${cleanStartDate}-${cleanEndDate}` : '',
      Modality: Modalities.filter((m) => m.isSelected)
        .map((m) => m.label)
        .join(','),
    };
    setDicomNode(selectedValue ?? '');
    onSubmitQuery(newFormData);
  }, [
    canSubmitQuery,
    studyDateRange.startDate,
    studyDateRange.endDate,
    birthDate,
    selectedValue,
    patientName,
    patientId,
    accessionNumber,
    description,
    referringPhysician,
    studyId,
    Modalities,
    setDicomNode,
    onSubmitQuery,
  ]);

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      if (!canSubmitQuery) return;

      const container = containerRef.current;
      const target = event.target as Node | null;
      if (!container || !target || !container.contains(target)) return;

      const targetElement = target as HTMLElement;
      if (targetElement.closest('[data-pacs-ignore-enter-query="true"]')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleQueryClick();
    };

    window.addEventListener('keydown', handleWindowKeyDown, true);
    return () => window.removeEventListener('keydown', handleWindowKeyDown, true);
  }, [canSubmitQuery, handleQueryClick]);

  const handleClearClick = () => {
    setPatientName('');
    setPatientId('');
    setBirthDate(null);
    setAccessionNumber('');
    setDescription('');
    setReferringPhysician('');
    setStudyId('');
    setStudyDateRange({ startDate: null, endDate: null });
    setModalities(
      Modalities.map((m) => ({ ...m, isSelected: false }))
    );
    setSelectedValue('');
    onClearAll();
  };

  return (
    <div ref={containerRef} className={styles["container"]}>
      {/* Server  부분 */}
      <div className={styles["server"]}>
        <div className={styles["label-input"]}>
          <div className={styles["label-input2"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
              <span>{PACS_CONTENTS_NAMES.PACS.selectserver}</span>
            </div>
            </div>
          </div>
          <Dropdown
            value={selectedValue}
            onChange={setSelectedValue}
            options={selectedOption}
            placeholder="Select a DICOM Node"
            className='server-dropdown'
          />
        </div>
        <div className={styles["server-action"]}>
          <Button
            className='action-control config'
            data-pacs-ignore-enter-query="true"
            onClick={() => handleOpenSettingsModal('pacsConnection')}
          >{PACS_CONTENTS_NAMES.PACS.pacsconfig}</Button>
        </div>
      </div>
      {/* Query 부분  */}
      <div className={styles["query"]}>
        <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.patientname}</span>
            </div>
            </div>
            <Input
              className='import-pacs-default-input'
              placeholder={PACS_CONTENTS_NAMES.PACS.patientname}
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
            />
          </div>
        </div>
        <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.patientid}</span>
            </div>
            </div>
            <Input
            className='import-pacs-default-input'
              placeholder={PACS_CONTENTS_NAMES.PACS.patientid}
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
            />
          </div>
        </div>
        <div className={styles["query-frame"]}>
          <div className={styles["date-picker-input"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.birthdate}</span>
            </div>
            </div>
            <DatePicker
            className='import-pacs-date-picker'
            value={birthDate ? birthDate : null} onChange={setBirthDate} />
          </div>
        </div>
        {/* <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
          <div className={styles["label"]}>
          <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.accessionnumber}</span>
            </div>
            </div>
          </div>
          <div className={styles["input-size-import-pacs"]}>
            <Input
              placeholder={PACS_CONTENTS_NAMES.PACS.accessionnumber}
              value={accessionNumber}
              onChange={(e) => setAccessionNumber(e.target.value)}
            />
          </div>
        </div> */}
        {/* <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
          <div className={styles["label"]}>
          <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.description}</span>
            </div>
            </div>
          </div>
          <div className={styles["input-size-import-pacs"]}>
            <Input
              placeholder={PACS_CONTENTS_NAMES.PACS.description}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div> */}
        {/* <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
          <div className={styles["label"]}>
          <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.referringphysician}</span>
            </div>
            </div>
          </div>
          <div className={styles["input-size-import-pacs"]}>
            <Input
              placeholder={PACS_CONTENTS_NAMES.PACS.referringphysician}
              value={referringPhysician}
              onChange={(e) => setReferringPhysician(e.target.value)}
            />
          </div>
        </div> */}
        <div className={styles["query-frame"]}>
          <div className={styles["number-input"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.studyid}</span>
            </div>
            </div>
            <Input
              className='import-pacs-default-input'
              placeholder={PACS_CONTENTS_NAMES.PACS.studyid}
              value={studyId}
              onChange={(e) => setStudyId(e.target.value)}
            />
          </div>
        </div>
        <div className={styles["query-frame"]}>
          <div className={styles["date-picker-input"]}>
            <div className={styles["label"]}>
            <div className={styles["label2"]}>
            <span>{PACS_CONTENTS_NAMES.PACS.studydate}</span>
            </div>
            </div>
            <DateRangePicker className='import-pacs-date-picker'
            value = {studyDateRange} onChange={setStudyDateRange} position="top" />
          </div>

        </div>
        {/* <div className={styles["query-frame"]}>
          <div className={styles["importpacs-box-title"]}>
            {PACS_CONTENTS_NAMES.PACS.modality}
          </div>
          <div className={styles["input-size-import-pacs"]}>
            <MultiSelectDropdown
              options={Modalities}
              onChange={setModalities}
              placeholder={PACS_CONTENTS_NAMES.PACS.modality}
              position="top"
            />
          </div>
        </div> */}
      </div>
      <div className={styles["actions"]}>
          <button
          // className='import-pacs-close'
          data-pacs-ignore-enter-query="true"
          onClick={handleClearClick}>
            {PACS_CONTENTS_NAMES.PACS.clear}
          </button>
          <button
            className={styles.downButton}
            onClick={handleQueryClick}
            disabled={!canSubmitQuery}
          >
            {PACS_CONTENTS_NAMES.PACS.query}
          </button>
      </div>
    </div>
  );
};

export default PACSModalSIdeBar;
